import { Icon as TablerIcon } from '@tabler/icons-react';
import classNames from 'classnames';
import * as Flowbite from 'flowbite-react';
import {
    cloneElement,
    ComponentProps,
    FormEventHandler,
    FunctionComponent,
    isValidElement,
    useEffect,
    useRef,
    useState,
} from 'react';
import { theme as buttonTheme } from 'shared/Button/theme';
import { theme as dropdownTheme } from 'shared/Dropdown/Dropdown';

const TargetTooltip = ({
    children,
    content,
    placement,
    ref,
    ...rest
}: Pick<
    ComponentProps<typeof Flowbite.Tooltip>,
    'children' | 'content' | 'placement' | 'ref'
>) => (
    <Flowbite.Tooltip content={content} placement={placement}>
        {isValidElement(children)
            ? cloneElement(children, { ...rest, ref } as object)
            : children}
    </Flowbite.Tooltip>
);

TargetTooltip.displayName = 'TargetTooltip';

const targetTheme = {
    ...buttonTheme,
    base: classNames(buttonTheme?.base, dropdownTheme.floating?.target),
};

export interface PopoverFormProps extends Omit<
    ComponentProps<typeof Flowbite.Popover>,
    'content' | 'onReset' | 'onSubmit'
> {
    dirty: boolean;
    icon: FunctionComponent<Pick<ComponentProps<TablerIcon>, 'className'>>;
    label: string;
    onClose: () => void;
    onSubmit: FormEventHandler<HTMLFormElement>;
    onReset?: FormEventHandler<HTMLFormElement>;
}

export const PopoverForm = ({
    children,
    dirty,
    label,
    icon: Icon,
    onClose,
    onReset,
    onSubmit,
}: PopoverFormProps) => {
    const [open, setOpen] = useState(false);

    const previousOpen = useRef(open);

    useEffect(() => {
        if (!open && previousOpen.current) {
            onClose();
        }

        previousOpen.current = open;
    }, [open, onClose]);

    return (
        <Flowbite.Popover
            aria-label={label}
            arrow={false}
            content={
                <form
                    aria-label={label}
                    className="bg-gray-50 p-3 rounded-lg"
                    onReset={onReset}
                    onSubmit={(event) => {
                        setOpen(false);
                        onSubmit(event);
                    }}
                >
                    {children}
                </form>
            }
            open={open}
            onOpenChange={setOpen}
        >
            <TargetTooltip content={label} placement="top">
                <Flowbite.Button
                    aria-expanded={open}
                    aria-label={label}
                    className={dirty ? 'text-primary-700' : undefined}
                    color="light"
                    size="iconSm"
                    theme={targetTheme}
                >
                    <Icon className="h-4 !rotate-0 w-4" />
                </Flowbite.Button>
            </TargetTooltip>
        </Flowbite.Popover>
    );
};
